import styled from "@emotion/styled";
import {
  Box,
  Chip,
  Fade,
  ListItemText,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs
} from "@mui/material";
import { ConfirmationDialog, Delayed, ManageCompetenciesEvent } from "klayowebshared";
import React, { SyntheticEvent } from "react";
import { Link } from "react-router-dom";
import { AppContext } from "../../common/AppContext";
import axiosClient from "../../common/AxiosClient";
import { track } from "../../common/track";
import { ErrorModel } from "../../common/models/ErrorModel";
import { Button } from "../../components/Button";
import { Paper } from "../../components/Paper";
import { AttributeTable } from "../../components/table/AttributeTable";
import { CategoryTable } from "../../components/table/CategoryTable";
import { TablePlaceholder } from "../../components/TablePlaceholder";
import { TextField } from "../../components/TextField";
import { ViewComponent } from "../../components/ViewComponent";
import { TableSearchDebounceTime } from "../../constants";
import { SettingsAttribute } from "../../data/settings/SettingsAttribute";
import { SettingsAttributeCategory } from "../../data/settings/SettingsAttributeCategory";
import { SettingsAttributeCategoryList } from "../../data/settings/SettingsAttributeCategoryList";
import { SettingsAttributeList } from "../../data/settings/SettingsAttributeList";
import AddIcon from "../../resources/images/icons-add.svg";
import AttributesIcon from "../../resources/images/icons-attribute.svg";
import CategoriesIcon from "../../resources/images/icons-departments.svg";
import FilterIcon from "../../resources/images/icons-filter.svg";
import RemoveIcon from "../../resources/images/icons-remove.svg";
import SearchIcon from "../../resources/images/icons-search.svg";

import { SettingCompetenciesSelector } from "../../components/selectors/SettingCompetenciesSelector";
import { withLocale, WithLocaleProps } from "../../providers/LocaleProvider";
import ChevronDownWhiteIcon from "../../resources/images/icons-chevron-down-white.svg";
import type {
  FilterCategory,
  PaginationModel,
  SelectedAttributeRow,
  SettingsAttributesRouterLocation,
  SettingsAttributesViewProps,
  SettingsAttributesViewState,
  SortModelItem
} from "../../types/views/settingsAttributes.types";
import { getSortByApiValue } from "../../utilities";
import { AddEditAttributeView } from "./AddEditAttributeView";
import { AddEditCategoryView } from "./AddEditCategoryView";
import { AttributeInformDialog } from "./AttributeInformDialog";
import { ImportCompetenciesFromIndustryStandard } from "./ImportCompetenciesFromIndustryStandard";
import LinkedJobDialog from "./LinkedJobDialog";

class SettingsAttributesViewBase extends ViewComponent<
  SettingsAttributesViewProps & WithLocaleProps,
  SettingsAttributesViewState
> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  debounceTimer: ReturnType<typeof setTimeout> | null;

  constructor(props: SettingsAttributesViewProps & WithLocaleProps) {
    super(props);

    this.state = {
      attributeSearch: null,
      categorySearch: null,
      actionTarget: null,
      actionCategory: null,
      actionAttribute: null,
      attributeOrderByConvertList: [
        { tableFieldName: "attribute", apiFieldName: "name" },
        { tableFieldName: "numJobs", apiFieldName: "numberOfJobAttributes" },
        { tableFieldName: "numEmployees", apiFieldName: "numberOfEmployeeAttributes" }
      ],
      categoryOrderByConvertList: [],
      attributeSortModel: [
        {
          field: "attribute",
          sort: "asc"
        }
      ],
      categorySortModel: [
        {
          field: "name",
          sort: "asc"
        }
      ],
      categoryError: null,
      attributeError: null,
      deletingCategory: null,
      deletingAttribute: null,
      isInformDialogShow: false,
      isFilterModalOpen: false,
      showLinkedJobDialog: false,
      selectedRow: null,
      selectedFilters: [],
      categoryIds: [],
      error: null,
      newAttribute: new SettingsAttribute(),
      newCategory: new SettingsAttributeCategory(),
      attributePaginationModel: {
        page: 0,
        pageSize: SettingsAttributeList.defaultPageSize
      },
      categoriesPaginationModel: {
        page: 0,
        pageSize: SettingsAttributeCategoryList.defaultPageSize
      },
      hasCategoriesLoaded: false,
      filteredCategories: null,
      isLoadingCategories: false,
      modalCategories: [],
      newJobDialog: null,
      isImportCompetenciesFromIndustryStandardOpen: false
    };

    this.debounceTimer = null;
  }
  loadFilterModalCategories = () => {
    axiosClient
      .get("/AttributeDefinition/AttributeCategory", {
        params: {
          pageSize: 1000 // Load all for filter modal
        }
      })
      .then((response) => {
        this.setState({
          modalCategories: response.data.attributeCategories.data,
          isLoadingCategories: false
        });
      });
  };
  loadCategories() {
    const { onLoadCategories } = this.props;
    const { categorySortModel, categoryOrderByConvertList } = this.state;
    const [{ field, sort }] = categorySortModel;
    const categoryOrderBy = getSortByApiValue(categoryOrderByConvertList as any, field, sort);

    track(ManageCompetenciesEvent.CategoriesViewed);
    onLoadCategories?.(false, null, null, categoryOrderBy);
    this.setState({ hasCategoriesLoaded: true });
  }
  componentDidMount() {
    track(ManageCompetenciesEvent.Viewed);
    window.scroll({ top: 0, left: 0, behavior: "smooth" });
    const { onLoadAttributes, onLoadCategories } = this.props;
    const {
      categorySortModel,
      attributeSortModel,
      attributeOrderByConvertList,
      categoryOrderByConvertList,
      attributeSearch,
      categoryIds
    } = this.state;
    const [{ field, sort }] = attributeSortModel;
    const attributeOrderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
    // const [model] = categorySortModel;

    onLoadAttributes?.(false, attributeSearch, null, attributeOrderBy, categoryIds);
    if (this.props.location.pathname === "/settings/competencies/categories") {
      this.loadCategories();
    }
  }
  componentDidUpdate(prevProps: SettingsAttributesViewProps): void {
    // Check if navigating to categories tab
    if (
      this.props.location.pathname === "/settings/competencies/categories" &&
      prevProps.location.pathname !== "/settings/competencies/categories" &&
      !this.state.hasCategoriesLoaded
    ) {
      this.loadCategories();
    }
    if (this.state.isLoadingCategories && prevProps.categories !== this.props.categories) {
      this.setState({
        isLoadingCategories: false
      });
    }
  }

  onAttributeSearch(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { onLoadAttributes } = this.props;
    const {
      attributePaginationModel: { pageSize },
      attributeSortModel,
      attributeOrderByConvertList,
      categoryIds
    } = this.state;
    const [{ field, sort }] = attributeSortModel;
    const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
    const keyword = e.target.value;
    clearTimeout(this.debounceTimer as any);
    this.debounceTimer = setTimeout(() => {
      if (keyword) track(ManageCompetenciesEvent.Searched, { searchtext: keyword });
      onLoadAttributes?.(true, keyword, pageSize, orderBy, categoryIds);
    }, TableSearchDebounceTime);
    const newModel = { page: 0, pageSize };
    this.setState({ attributeSearch: keyword, attributePaginationModel: newModel });
  }

  onAttributesPaginationModelChange(model: PaginationModel): void {
    const { page, pageSize } = model;
    this.setState({ attributePaginationModel: model });
    const { onLoadAttributesPageSize } = this.props;
    const { attributeSearch, attributeSortModel, attributeOrderByConvertList, categoryIds } =
      this.state;
    const [{ field, sort }] = attributeSortModel;
    const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
    if (onLoadAttributesPageSize) {
      onLoadAttributesPageSize(page + 1, pageSize, attributeSearch, orderBy, categoryIds);
    }
  }

  onAttributesSortModelChange(sortModel: SortModelItem[]): void {
    const { onLoadAttributes } = this.props;
    const {
      attributeSearch,
      attributePaginationModel: { pageSize },
      attributeOrderByConvertList,
      categoryIds
    } = this.state;
    const [{ field, sort }] = sortModel;
    const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
    onLoadAttributes?.(true, attributeSearch, pageSize, orderBy, categoryIds);
    const newModel = { page: 0, pageSize };
    this.setState({ attributeSortModel: sortModel, attributePaginationModel: newModel });
  }

  onCategorySearch(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { onLoadCategories } = this.props;
    const {
      categoriesPaginationModel: { pageSize },
      categorySortModel,
      categoryOrderByConvertList
    } = this.state;
    const [{ field, sort }] = categorySortModel;
    const orderBy = getSortByApiValue(categoryOrderByConvertList as any, field, sort);
    clearTimeout(this.debounceTimer as any);
    this.debounceTimer = setTimeout(() => {
      onLoadCategories?.(true, e.target.value, pageSize, orderBy);
    }, TableSearchDebounceTime);
    const newModel = { page: 0, pageSize };
    this.setState({ categorySearch: e.target.value, categoriesPaginationModel: newModel });
  }

  onCategoriesPaginationModelChange(model: PaginationModel): void {
    const { page, pageSize } = model;
    this.setState({ categoriesPaginationModel: model });
    const { onLoadCategoriesPageSize } = this.props;
    const { categorySearch, categorySortModel, categoryOrderByConvertList } = this.state;
    const [{ field, sort }] = categorySortModel;
    const orderBy = getSortByApiValue(categoryOrderByConvertList as any, field, sort);
    if (onLoadCategoriesPageSize) {
      onLoadCategoriesPageSize(page + 1, pageSize, categorySearch, orderBy);
    }
  }

  onCategoriesSortModelChange(sortModel: SortModelItem[]): void {
    const { onLoadCategories } = this.props;
    const {
      categorySearch,
      categoryOrderByConvertList,
      categoriesPaginationModel: { pageSize }
    } = this.state;
    const [{ field, sort }] = sortModel;
    const orderBy = getSortByApiValue(categoryOrderByConvertList as any, field, sort);
    onLoadCategories?.(true, categorySearch, pageSize, orderBy);
    const newModel = { page: 0, pageSize };
    this.setState({ categorySortModel: sortModel, categoriesPaginationModel: newModel });
  }

  onViewChange(location: SettingsAttributesRouterLocation): void {
    if (
      this.pathEquals("/settings/competencies") ||
      this.pathEquals("/settings/competencies/categories")
    )
      this.resetErrors();
  }

  resetErrors(): void {
    this.setState({
      categoryError: null,
      attributeError: null,
      errorSnackbar: null,
      newAttribute: new SettingsAttribute(),
      newCategory: new SettingsAttributeCategory()
    });
  }

  onModeChange(e: SyntheticEvent, showCategories: boolean): void {
    if (showCategories) this.loadCategories();
  }

  onCancelAddEditAttribute(e: SyntheticEvent | null): void {
    const { history, onCancelAddEditAttribute, location } = this.props;
    const { showAddEditAttributeDialog } = this.state;

    this.setState({ attributeError: null, newCategory: new SettingsAttributeCategory() });

    const queryParams = new URLSearchParams(location.search);
    const navigateMode = queryParams.get("navigateMode");

    if (navigateMode === "task") {
      history.push("/settings/tasks");
      return;
    }

    if (!showAddEditAttributeDialog) history.push("/settings/competencies");
    if (onCancelAddEditAttribute) onCancelAddEditAttribute();
  }

  onSaveAttribute(attribute: SettingsAttribute, returnToList: boolean): void {
    const { history, onSaveAttribute } = this.props;
    const {
      categorySearch,
      attributeSearch,
      attributeSortModel,
      attributeOrderByConvertList,
      categoryIds,
      attributePaginationModel
    } = this.state;

    const [{ field, sort }] = attributeSortModel;
    const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
    const isAddingNew = attribute.attributeId === null || attribute.attributeId === undefined;
    if (isAddingNew) {
      track(
        returnToList
          ? ManageCompetenciesEvent.NewCompetencyCreate
          : ManageCompetenciesEvent.NewCompetencySaveAndCreate
      );
    }
    if (onSaveAttribute)
      onSaveAttribute(
        attribute,
        returnToList,
        attributeSearch,
        categorySearch,
        orderBy,
        categoryIds,
        attributePaginationModel.page + 1,
        attributePaginationModel.pageSize
      )
        .then(() => {
          const { translate } = this.props;
          const successMessage = isAddingNew
            ? translate("settingsViews.competencies.competencyCreated")
            : translate("settingsViews.competencies.competencyUpdated");
          if (returnToList) this.onCancelAddEditAttribute(null);
          this.setState({ successSnackbar: successMessage });
          if (!returnToList) {
            this.setState(
              {
                attributeError: null,
                newAttribute: new SettingsAttribute(),
                successSnackbar: successMessage
              },
              () => history.push("/settings/competencies/new")
            );
          }
        })
        .catch((e: Error) => {
          this.setState({ attributeError: ErrorModel.parseServerError(e) });
        }) as any;
  }

  onCancelAddEditCategory(e?: SyntheticEvent): void {
    const { history, onCancelAddEditCategory } = this.props;
    const { showAddEditCategoryDialog } = this.state;

    this.setState({ categoryError: null, newCategory: new SettingsAttributeCategory() });

    if (!showAddEditCategoryDialog) history.push("/settings/competencies/categories");
    if (onCancelAddEditCategory) onCancelAddEditCategory();
  }

  onSaveCategory(categoryId: string | null, name?: string): void {
    const { onSaveCategory } = this.props;
    const { categorySearch, categoriesPaginationModel } = this.state;

    if (onSaveCategory)
      onSaveCategory(
        categoryId,
        categorySearch,
        categoriesPaginationModel.page + 1,
        categoriesPaginationModel.pageSize
      )
        .then(() => {
          this.onCancelAddEditCategory();
          this.setState({
            categoryError: null,
            newCategory: new SettingsAttributeCategory(),
            successSnackbar: this.props.translate("settingsViews.competencies.categoryCreated")
          });
        })
        .catch((e: Error) => {
          this.setState({ categoryError: ErrorModel.parseServerError(e) });
        });
  }

  isPath(path: string): boolean {
    return this.props.location.pathname.startsWith(path);
  }

  onCategoryAction(e: React.MouseEvent<HTMLElement>, category: SettingsAttributeCategory): void {
    //if (this.locationMenu) this.locationMenu.open(e.target);
    this.setState({ actionTarget: e.target as HTMLElement, actionCategory: category });
  }

  onAttributeAction(e: React.MouseEvent<HTMLElement>, attribute: SettingsAttribute): void {
    //if (this.locationMenu) this.locationMenu.open(e.target);
    this.setState({ actionTarget: e.target as HTMLElement, actionAttribute: attribute });
  }

  onEditCategory() {
    const { actionCategory } = this.state;
    track(ManageCompetenciesEvent.CategoriesMenuOption, { option: "edit" });
    this.onCloseActionMenu();

    if (actionCategory) {
      this.props.history.push(
        "/settings/competencies/categories/edit/" + actionCategory.categoryId
      );
    }
  }

  onDeleteCategory() {
    const { actionCategory } = this.state;
    track(ManageCompetenciesEvent.CategoriesMenuOption, { option: "delete" });

    if (actionCategory) {
      if (actionCategory.isInUsed) this.setState({ deletingCategory: actionCategory });
      else this.deleteCategory(actionCategory);
    }

    this.onCloseActionMenu();
  }

  onDeleteCategoryConfirmed() {
    const { deletingCategory } = this.state;
    if (deletingCategory) {
      this.deleteCategory(deletingCategory);
    }
    this.setState({ deletingCategory: null });
  }

  onCancelDeleteCategory() {
    this.setState({ deletingCategory: null });
  }

  onEditAttribute() {
    const { actionAttribute } = this.state;
    track(ManageCompetenciesEvent.MenuOption, { option: "edit" });
    this.onCloseActionMenu();
    if (actionAttribute) {
      this.props.history.push("/settings/competencies/edit/" + actionAttribute.attributeId);
    }
  }

  onDeleteAttribute() {
    const { actionAttribute } = this.state;
    track(ManageCompetenciesEvent.MenuOption, { option: "delete" });
    if (actionAttribute) {
      if (actionAttribute.isJob || actionAttribute.isInUsed || actionAttribute.isTasked)
        this.setState({ deletingAttribute: actionAttribute });
      else this.deleteAttribute(actionAttribute);
    }
    this.onCloseActionMenu();
  }

  onDeleteAttributeConfirmed() {
    const { deletingAttribute } = this.state;
    if (deletingAttribute) {
      this.deleteAttribute(deletingAttribute);
    }
    this.setState({ deletingAttribute: null });
  }

  onCancelDeleteAttribute() {
    this.setState({ deletingAttribute: null });
  }

  onCloseActionMenu() {
    this.setState({ actionTarget: null, actionCategory: null, actionAttribute: null });
  }

  onCloseAttributeInformDialog() {
    this.setState({ isInformDialogShow: false });
  }

  onOpenAttributeInformDialog() {
    this.setState({ isInformDialogShow: true });
  }

  deleteAttribute(attribute: SettingsAttribute): void {
    const { attributes, onLoadAttributes } = this.props;
    const {
      attributeSearch,
      attributeOrderByConvertList,
      attributeSortModel,
      categoryIds,
      attributePaginationModel
    } = this.state;
    const [{ field, sort }] = attributeSortModel;
    this.context?.setLoading?.("deleting", true);

    axiosClient
      .delete("/AttributeDefinition", {
        data: {
          attributeDefinitionId: attribute.attributeId
        }
      })
      .then(() => {
        if (attributes) {
          attributes.delete(attribute);
        }
        this.setState({
          successSnackbar: this.props.translate("settingsViews.competencies.competencyDeleted")
        });
        const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
        if (onLoadAttributes)
          onLoadAttributes(
            true,
            attributeSearch,
            attributePaginationModel.pageSize,
            orderBy,
            categoryIds,
            attributePaginationModel.page + 1
          );
      })
      .finally(() => {
        this.context?.setLoading?.("deleting", false);
      });
  }

  deleteCategory(category: SettingsAttributeCategory): void {
    const { categories, onLoadAttributes, onLoadCategories } = this.props;
    const {
      categorySearch,
      attributeSearch,
      categoryIds,
      attributeOrderByConvertList,
      attributeSortModel,
      attributePaginationModel
    } = this.state;
    const [{ field, sort }] = attributeSortModel;
    this.context?.setLoading?.("deleting", true);

    axiosClient
      .delete("/AttributeDefinition/AttributeCategory", {
        data: {
          attributeCategoryId: category.categoryId,
          isForcing: true
        }
      })
      .then(() => {
        if (categories) {
          categories.deleteCategory(category);
        }
        this.setState({
          successSnackbar: this.props.translate("settingsViews.competencies.categoryDeleted")
        });
        const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
        if (onLoadAttributes)
          onLoadAttributes(
            true,
            attributeSearch,
            attributePaginationModel.pageSize,
            orderBy,
            categoryIds,
            attributePaginationModel.page + 1
          );
        if (onLoadCategories) {
          const {
            categoriesPaginationModel: { pageSize, page },
            categorySortModel,
            categoryOrderByConvertList
          } = this.state;
          const [{ field, sort }] = categorySortModel;
          const orderBy = getSortByApiValue(categoryOrderByConvertList as any, field, sort);
          onLoadCategories(true, categorySearch, pageSize, orderBy, page + 1);
        }
      })
      .finally(() => {
        this.context?.setLoading?.("deleting", false);
      });
  }

  onAttributeSelect(e: SyntheticEvent, attr: SettingsAttribute): void {
    track(ManageCompetenciesEvent.RowClicked);
    this.props.history.push("/settings/competencies/edit/" + attr.attributeId);
  }

  onCategorySelect(e: SyntheticEvent, cat: SettingsAttributeCategory): void {
    this.props.history.push("/settings/competencies/categories/edit/" + cat.categoryId);
  }
  handleOpenFilterModal = () => {
    this.setState({
      isFilterModalOpen: true,
      isLoadingCategories: true
    });
    // Load all categories when opening modal
    this.loadFilterModalCategories();
  };

  handleCloseFilterModal = () => {
    this.setState({ isFilterModalOpen: false, isLoadingCategories: false });
  };

  handleSaveFilters = (selectedCategories: FilterCategory[]): void => {
    const {
      attributeSearch,
      attributeSortModel,
      attributeOrderByConvertList,
      attributePaginationModel
    } = this.state;
    const [{ field, sort }] = attributeSortModel;
    const categoryIds = selectedCategories.map((cat) => cat.attributeCategoryId);
    track(ManageCompetenciesEvent.FilterSet, {
      filters: selectedCategories.map((cat) => cat.name)
    });
    this.setState(
      {
        selectedFilters: selectedCategories,
        isFilterModalOpen: false,
        categoryIds
      },
      () => {
        //
        const { onLoadAttributes } = this.props;
        const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
        if (onLoadAttributes) {
          onLoadAttributes(
            true,
            attributeSearch,
            attributePaginationModel.pageSize,
            orderBy,
            categoryIds
          );
        }
      }
    );
  };

  handleRemoveCategories = (attributeToRemove: FilterCategory): void => {
    const {
      selectedFilters,
      attributeSearch,
      attributeOrderByConvertList,
      attributeSortModel,
      attributePaginationModel
    } = this.state;
    const [{ field, sort }] = attributeSortModel;
    const updatedFilters = selectedFilters.filter(
      (attr: FilterCategory) => attr.attributeCategoryId !== attributeToRemove.attributeCategoryId
    );

    const updatedCategoryIds = updatedFilters.map(
      (filter: FilterCategory) => filter.attributeCategoryId
    );
    this.setState(
      {
        selectedFilters: updatedFilters,
        categoryIds: updatedCategoryIds
      },
      () => {
        const { onLoadAttributes } = this.props;

        const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);
        if (onLoadAttributes) {
          onLoadAttributes(
            true,
            attributeSearch,
            attributePaginationModel.pageSize,
            orderBy,
            updatedCategoryIds
          );
        }
      }
    );
  };
  handleSearchFilters = (searchText: string | null | undefined): void => {
    const { modalCategories } = this.state;

    if (!searchText || searchText.trim() === "") {
      this.setState({ filteredCategories: null });
      return;
    }

    const filteredCategories = modalCategories.filter((category: FilterCategory) =>
      category.name.toLowerCase().includes(searchText.toLowerCase())
    );

    this.setState({ filteredCategories: filteredCategories });
  };
  handleClearAll = () => {
    const {
      attributeSearch,
      attributeOrderByConvertList,
      attributeSortModel,
      attributePaginationModel
    } = this.state;
    const [{ field, sort }] = attributeSortModel;
    this.setState(
      {
        selectedFilters: [],
        categoryIds: []
      },
      () => {
        const { onLoadAttributes } = this.props;
        const orderBy = getSortByApiValue(attributeOrderByConvertList as any, field, sort);

        if (onLoadAttributes) {
          onLoadAttributes(
            true,
            attributeSearch,
            attributePaginationModel.pageSize,
            orderBy,
            [] as any
          );
        }
      }
    );
  };

  handleOpenLinkedJobDialog = (e: SyntheticEvent, row: SelectedAttributeRow): void => {
    track(ManageCompetenciesEvent.JobsClicked);
    this.setState({
      showLinkedJobDialog: true,
      selectedRow: row
    });
  };

  handleCloseLinkedJobDialog = (): void => {
    this.setState({
      showLinkedJobDialog: false,
      selectedRow: null
    });
  };

  handleCloseNewJobDialog = (): void => {
    this.setState({
      newJobDialog: null
    });
  };

  handleClose(e: SyntheticEvent): void {
    this.setState({ newJobDialog: null });
  }

  goToNewJob() {
    this.setState({ newJobDialog: null });
    this.props.history.push("/settings/competencies/new");
  }

  render() {
    const {
      organization,
      user,
      theme,
      location,
      orgSettings,
      history,
      onShowNewAttributeDialog,
      onShowNewCategoryDialog,
      categories,
      attributes,
      onNavigation,
      onBlockNavigation,
      onAllowNavigation,
      isLoading,
      translate
    } = this.props;
    const {
      attributeSortModel,
      categorySortModel,
      attributeSearch,
      categorySearch,
      actionTarget,
      categoryError,
      attributeError,
      deletingCategory,
      deletingAttribute,
      newAttribute,
      newCategory,
      isInformDialogShow,
      attributePaginationModel,
      categoriesPaginationModel,
      isFilterModalOpen,
      selectedFilters,
      newJobDialog
    } = this.state;

    const path = location.pathname;
    const showCategories = path.startsWith("/settings/competencies/categories");
    const editMode = path.includes("edit");

    const editingAttribute = path.startsWith("/settings/competencies/edit/");
    const showAddEditAttributeView =
      path.startsWith("/settings/competencies/new") || editingAttribute;

    const editingCategory = path.startsWith("/settings/competencies/categories/edit/");
    const showAddEditCategoryView =
      path.startsWith("/settings/competencies/categories/new") || editingCategory;
    const showRoot = !showAddEditAttributeView && !showAddEditCategoryView;

    const queryParams = new URLSearchParams(location.search);
    const navigateMode = queryParams.get("navigateMode");

    let editCategory = null;
    let editCategoryId = null;
    if (editingCategory) {
      editCategoryId = path.replace("/settings/competencies/categories/edit/", "");
      editCategory = {
        attributeCategoryId: editCategoryId
      };
    }

    let editAttribute = null;
    let editAttributeId = null;
    if (editingAttribute) editAttributeId = path.replace("/settings/competencies/edit/", "");
    // if (attributes) editAttribute = attributes.get(editAttributeId);
    if (editAttributeId)
      editAttribute = {
        attributeDefinitionId: editAttributeId
      };

    const categoryFilterItems = [
      { id: "search", field: "name", operator: "contains", value: categorySearch }
    ];
    const attributeFilterItems = [
      { id: "search", field: "attribute", operator: "contains", value: attributeSearch }
    ];

    return (
      <div>
        <Menu
          open={newJobDialog !== null}
          anchorEl={newJobDialog}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          onClose={this.handleClose.bind(this)}
        >
          <MenuItem
            onClick={() => {
              track(ManageCompetenciesEvent.NewCompetencyEmpty);
              this.goToNewJob();
            }}
          >
            <ListItemText>
              {translate("settingsViews.competencies.newEmptyCompetency")}
            </ListItemText>
          </MenuItem>

          <MenuItem
              onClick={() => {
                track(ManageCompetenciesEvent.NewCompetencyIntelligence);
                this.setState({
                  isImportCompetenciesFromIndustryStandardOpen: true,
                  newJobDialog: null
                });
              }}
            >
              <ListItemText>
                {translate("settingsViews.competencies.startFromKlayoIntelligence")}
              </ListItemText>
            </MenuItem>
        </Menu>
        {showRoot && (
          <Paper
            theme={theme}
            padding={{ xs: "40px 24px", md: "56px 64px!important" }}
            borderFromBreakpoint='md'
          >
            <StyledAttributeTitle>
              {translate("settingsViews.competencies.title")}
            </StyledAttributeTitle>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                centered={true}
                value={showCategories ? "categories" : "competencies"}
                variant='fullWidth'
                sx={{ width: "100%" }}
              >
                <Tab
                  label={translate("settingsViews.competencies.competenciesTab")}
                  to={"/settings/competencies"}
                  component={Link}
                  value='competencies'
                />
                <Tab
                  label={translate("settingsViews.competencies.categoriesTab")}
                  to={"/settings/competencies/categories"}
                  component={Link}
                  value='categories'
                />
              </Tabs>
            </Box>

            {!showCategories ? (
              <div>
                <StyledAttributeSection
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  spacing={2}
                >
                  <div style={{ display: "flex", gap: "24px" }}>
                    <TextField
                      value={attributeSearch}
                      dense={true}
                      placeholder={translate("settingsViews.competencies.searchCompetencies")}
                      disabled={user === null}
                      autoComplete={false}
                      leadingIcon={<SearchIcon />}
                      fullWidth={true}
                      onChange={this.onAttributeSearch.bind(this)}
                      sx={{ maxWidth: { md: "300px" }, width: "300px" }}
                    />
                    <Button
                      className='klayo-button_label'
                      label={translate("settingsViews.competencies.filters")}
                      theme={theme}
                      size='md'
                      startIcon={<FilterIcon />}
                      sx={{
                        width: "180px"
                      }}
                      onClick={this.handleOpenFilterModal}
                    />
                  </div>

                  <div className='klaro_regulations-button-container'>
                      <Button
                        size='md'
                        theme={theme}
                        variant='filled'
                        label={translate("settingsViews.competencies.newCompetency")}
                        className='klaro_regulations-button-add-regulation'
                        onClick={() => {
                          track(ManageCompetenciesEvent.NewCompetency);
                          this.goToNewJob();
                        }}
                        startIcon={<AddIcon />}
                      />
                      <div className='klaro_regulations-vertical-line'></div>
                      <div className='klaro_regulations-domain-category'>
                        <ChevronDownWhiteIcon
                          onClick={(event: React.MouseEvent<SVGSVGElement>) => {
                            this.setState({
                              newJobDialog: event.currentTarget as unknown as HTMLElement
                            });
                          }}
                        />
                      </div>
                    </div>
                </StyledAttributeSection>
                {selectedFilters.length > 0 && (
                  <Stack
                    direction='row'
                    alignItems='center'
                    flexWrap='wrap'
                    sx={{
                      margin: "-4px 0 0 -4px"
                    }}
                  >
                    {selectedFilters.map((attribute: FilterCategory) => (
                      <Chip
                        key={attribute.attributeCategoryId}
                        label={
                          <span>
                            {translate("settingsViews.competencies.categoryFilter")}:{" "}
                            <b>{attribute.name}</b>
                          </span>
                        }
                        onDelete={() => this.handleRemoveCategories(attribute)}
                        deleteIcon={<RemoveIcon />}
                        sx={{
                          margin: "4px 0 0 4px"
                        }}
                      />
                    ))}
                    <Box
                      component='span'
                      onClick={this.handleClearAll}
                      sx={{
                        color: "#0027da",
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline"
                        },
                        margin: "4px 0 0 4px"
                      }}
                    >
                      {translate("settingsViews.competencies.clearAll")}
                    </Box>
                  </Stack>
                )}
                <Delayed>
                  <AttributeTable
                    disableSelection={false}
                    minHeight='300px'
                    rowHasAction={true}
                    theme={theme}
                    translate={translate}
                    showHeaderFromBreakpoint='md'
                    filterMode='server'
                    filterItems={attributeFilterItems}
                    dense={true}
                    paginationMode='server'
                    totalCount={this.props.attributes?.totalCount}
                    paginationModel={attributePaginationModel}
                    onPaginationModelChange={this.onAttributesPaginationModelChange.bind(this)}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    onAttributeSelect={this.onAttributeSelect.bind(this) as any}
                    onJobSelect={this.handleOpenLinkedJobDialog}
                    onAttributeAction={this.onAttributeAction.bind(this) as any}
                    onSortModelChange={this.onAttributesSortModelChange.bind(this) as any}
                    sortingMode='server'
                    sortModel={attributeSortModel as any}
                    paper={false}
                    columns={[
                      {
                        type: AttributeTable.columns.attribute,
                        label: translate("settingsViews.competencies.competencyColumn"),
                        visibleFromBreakpoint: "xs"
                      },
                      { type: AttributeTable.columns.numJobs, visibleFromBreakpoint: "md" },
                      { type: AttributeTable.columns.numEmployees, visibleFromBreakpoint: "md" },
                      {
                        type: AttributeTable.columns.hasTrainingCourses,
                        visibleFromBreakpoint: "md"
                      },
                      { type: AttributeTable.columns.actions }
                    ]}
                    rows={attributes ? attributes.attributes : null}
                    noRowsComponent={
                      <TablePlaceholder
                        text={translate("settingsViews.competencies.noCompetenciesPlaceholder")}
                        icon={<AttributesIcon />}
                      />
                    }
                    keyword={attributeSearch ?? undefined}
                  />
                </Delayed>
                <Menu
                  open={actionTarget !== null}
                  anchorEl={actionTarget}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                  }}
                  onClose={this.onCloseActionMenu.bind(this)}
                >
                  <MenuItem onClick={this.onEditAttribute.bind(this)}>
                    <ListItemText>
                      {translate("settingsViews.competencies.editCompetency")}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={this.onDeleteAttribute.bind(this)}>
                    <ListItemText>
                      {translate("settingsViews.competencies.deleteCompetency")}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </div>
            ) : (
              <div>
                <StyledAttributeSection
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  spacing={2}
                >
                  <TextField
                    value={categorySearch}
                    dense={true}
                    placeholder={translate("settingsViews.competencies.searchCategories")}
                    disabled={user === null}
                    autoComplete={false}
                    fullWidth={true}
                    leadingIcon={<SearchIcon />}
                    onChange={this.onCategorySearch.bind(this)}
                    sx={{ maxWidth: { md: "300px" } }}
                  />
                  <Button
                    path={"/settings/competencies/categories/new"}
                    size='md'
                    theme={theme}
                    variant='filled'
                    startIcon={<AddIcon />}
                    showLabelFromBreakpoint='md'
                    label={translate("settingsViews.competencies.newCategory")}
                    onClick={() => track(ManageCompetenciesEvent.CategoriesNew)}
                  />
                </StyledAttributeSection>
                <CategoryTable
                  theme={theme}
                  showHeaderFromBreakpoint='md'
                  minHeight='300px'
                  filterMode='server'
                  filterItems={categoryFilterItems}
                  dense={true}
                  paginationMode='server'
                  totalCount={this.props.categories?.totalCount}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  paginationModel={categoriesPaginationModel}
                  onPaginationModelChange={this.onCategoriesPaginationModelChange.bind(this)}
                  onCategorySelect={this.onCategorySelect.bind(this) as any}
                  onCategoryAction={this.onCategoryAction.bind(this) as any}
                  onSortModelChange={this.onCategoriesSortModelChange.bind(this) as any}
                  sortingMode='server'
                  sortModel={categorySortModel as any}
                  paper={false}
                  rows={categories ? categories.categories : null}
                  noRowsComponent={
                    <TablePlaceholder
                      text={translate("settingsViews.competencies.noCategoriesPlaceholder")}
                      icon={<CategoriesIcon />}
                    />
                  }
                  keyword={categorySearch ?? undefined}
                  columns={[
                    {
                      type: CategoryTable.columns.name,
                      label: translate("settingsViews.competencies.competencyCategoryColumn")
                    },
                    {
                      type: CategoryTable.columns.numberOfAttributeDefinitions,
                      label: translate("settingsViews.competencies.competenciesColumn")
                    },
                    { type: CategoryTable.columns.actions }
                  ]}
                />

                <Menu
                  open={actionTarget !== null}
                  anchorEl={actionTarget}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                  }}
                  onClose={this.onCloseActionMenu.bind(this)}
                >
                  <MenuItem onClick={this.onEditCategory.bind(this)}>
                    <ListItemText>
                      {translate("settingsViews.competencies.editCategory")}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={this.onDeleteCategory.bind(this)}>
                    <ListItemText>
                      {translate("settingsViews.competencies.deleteCategory")}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Paper>
        )}

        {showAddEditAttributeView && (
          <AddEditAttributeView
            {...(this.props as any)}
            className={"klayo__add-edit-attribute-setting"}
            dialogMode={false}
            organization={organization}
            orgSettings={orgSettings}
            editMode={editMode}
            theme={theme}
            error={attributeError}
            onShowNewCategoryDialog={onShowNewCategoryDialog}
            onShowNewAttributeDialog={onShowNewAttributeDialog}
            attribute={editMode ? editAttribute : newAttribute}
            attributeId={editAttributeId ?? undefined}
            onBlockNavigation={onBlockNavigation}
            onAllowNavigation={onAllowNavigation}
            onNavigation={onNavigation}
            onOpenDialog={this.onOpenAttributeInformDialog.bind(this)}
            onSave={this.onSaveAttribute.bind(this)}
            onCancel={this.onCancelAddEditAttribute.bind(this)}
            navigateMode={navigateMode ?? undefined}
          />
        )}
        <LinkedJobDialog
          open={this.state.showLinkedJobDialog}
          onClose={this.handleCloseLinkedJobDialog}
          row={{
            attributeDefinitionId: this.state.selectedRow?.attributeId
          }}
          theme={theme}
        />

        {showAddEditCategoryView && (
          <AddEditCategoryView
            editMode={editMode}
            theme={theme}
            dialogMode={false}
            category={
              editMode && editCategory ? new SettingsAttributeCategory(editCategory) : newCategory
            }
            categoryId={editCategoryId ?? undefined}
            error={categoryError}
            onBlockNavigation={onBlockNavigation}
            onAllowNavigation={onAllowNavigation}
            onNavigation={onNavigation}
            onSave={this.onSaveCategory.bind(this)}
            onCancel={this.onCancelAddEditCategory.bind(this)}
          />
        )}

        {deletingCategory && (
          <ConfirmationDialog
            theme={theme}
            title={translate("settingsViews.competencies.deleteCategoryTitle")}
            question={translate("settingsViews.competencies.deleteCategoryConfirm")}
            cancelButton={translate("common.no")}
            acceptButton={translate("common.yes")}
            acceptDanger={true}
            onCancel={this.onCancelDeleteCategory.bind(this)}
            onAccept={this.onDeleteCategoryConfirmed.bind(this)}
          />
        )}

        {deletingAttribute && (
          <ConfirmationDialog
            theme={theme}
            title={translate("settingsViews.competencies.deleteCompetencyTitle")}
            question={translate("settingsViews.competencies.deleteCompetencyConfirm")}
            cancelButton={translate("common.cancel")}
            acceptButton={translate("settingsViews.competencies.deleteCompetency")}
            acceptDanger={true}
            onCancel={this.onCancelDeleteAttribute.bind(this)}
            onAccept={this.onDeleteAttributeConfirmed.bind(this)}
          />
        )}

        {isInformDialogShow && (
          <AttributeInformDialog
            onClose={this.onCloseAttributeInformDialog.bind(this)}
            theme={theme}
            {...this.props}
          />
        )}
        <SettingCompetenciesSelector
          open={isFilterModalOpen}
          categories={this.state.filteredCategories || this.state.modalCategories || []}
          theme={theme}
          onClose={this.handleCloseFilterModal}
          onSave={this.handleSaveFilters}
          initialSelectedCategories={selectedFilters}
          onSearch={this.handleSearchFilters}
          isLoading={this.state.isLoadingCategories}
        />

        {this.renderSnackbar()}

        <Fade
          in={this.state.isImportCompetenciesFromIndustryStandardOpen}
          timeout={300}
          unmountOnExit
        >
          <div>
            <ImportCompetenciesFromIndustryStandard
              theme={theme}
              open={this.state.isImportCompetenciesFromIndustryStandardOpen}
              onClose={() => this.setState({ isImportCompetenciesFromIndustryStandardOpen: false })}
              organization={organization}
            />
          </div>
        </Fade>
      </div>
    );
  }
}

const StyledAttributeSection = styled(Stack)`
  padding: 40px 0px 24px 0px;

  @media (max-width: 600px) {
    padding: 32px 0px 24px 0px;
  }
`;

const StyledAttributeTitle = styled.h1`
  @media (max-width: 600px) {
    margin-bottom: 32px !important;
  }
`;

export const SettingsAttributesView = withLocale(SettingsAttributesViewBase);
